import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect } from "react";
import {
  isViewOnlyModeAtom,
  isDraftModeExcludingTemplatesAtom,
  streamingProgressAtom,
} from "@zapier/canvas-state";
import { useCopilotChat } from "@zapier/ai-toolkit/client";
import type { Message } from "@ai-sdk/ui-utils";

import { renderZslToCanvasSetter } from "../atoms/renderZslToCanvasSetter";
import { outlineAtom } from "../atoms/outlineAtom";
import { aiPromptAtom } from "../atoms/aiPromptAtom";
import { useSyncZslStreamToOutline } from "./useSyncZslStreamToOutline";
import {
  promptToZslErrorAtom,
  isPromptToZslCompleteAtom,
  isStreamingZslAtom,
} from "../atoms/streamingStatusAtoms";
import { tuningPromptsAtom } from "../atoms/tuningPromptsAtom";

export function usePromptToZslToCanvas() {
  const renderZslToCanvas = useSetAtom(renderZslToCanvasSetter);
  const setStringOutline = useSetAtom(outlineAtom);
  const [prompt, setPrompt] = useAtom(aiPromptAtom);
  const setTuningPrompts = useSetAtom(tuningPromptsAtom);
  const setError = useSetAtom(promptToZslErrorAtom);
  const { setMessages } = useCopilotChat();
  const setIsViewOnlyMode = useSetAtom(isViewOnlyModeAtom);
  const setIsStreamingZsl = useSetAtom(isStreamingZslAtom);
  const setStreamingProgress = useSetAtom(streamingProgressAtom);
  const setIsComplete = useSetAtom(isPromptToZslCompleteAtom);
  const isDraftModeNotTemplates = useAtomValue(
    isDraftModeExcludingTemplatesAtom,
  );

  useEffect(() => {
    /* Be aware that if you use another var other than isDraftModeNotTemplates,
     * sometimes there will be multiple messages, and they will never finish (green check mark in copilot)
     * */
    if (isDraftModeNotTemplates) {
      // Set messages using the appropriate setMessages function
      if (prompt) {
        const messages: Message[] = [
          {
            role: "assistant",
            content: "", // Assistant will invoke the tool next
            id: "assistant-tool-call",
            createdAt: new Date(),
            parts: [
              { type: "text", text: prompt },
              {
                type: "tool-invocation",
                toolInvocation: {
                  state: "call",
                  toolName: "create_system",
                  toolCallId: "create_system",
                  args: {
                    prompt,
                  },
                },
              },
            ],
          },
        ];
        setMessages(messages);
      }
    }
  }, [prompt, setMessages, isDraftModeNotTemplates]);

  const { handleSubmit } = useSyncZslStreamToOutline({
    api: "/api/zsl/v2/combined_stream",
    async onMessage() {},
    async onComplete(zsl) {
      setIsStreamingZsl(false);
      setStreamingProgress(0);
      setIsViewOnlyMode(false);

      // Update the last tool invocation with the zsl result
      setMessages((prevMessages: Message[]) => {
        return prevMessages.map((message: Message) => {
          const hasUpdateCall = message?.parts?.some(
            (invocation: any) =>
              invocation.toolInvocation?.toolName === "create_system" &&
              invocation.toolInvocation?.state === "call",
          );

          if (hasUpdateCall) {
            const completedToolInvocation = {
              ...message,
              parts: message.parts?.map((part: any) => {
                if (
                  part.type === "tool-invocation" &&
                  part.toolInvocation?.toolName === "create_system" &&
                  part.toolInvocation?.state === "call"
                ) {
                  return {
                    ...part,
                    toolInvocation: {
                      ...part.toolInvocation,
                      state: "result",
                      args: {
                        prompt,
                      },
                      result: {
                        content: [
                          {
                            text: `Canvas updated`,
                            zsl,
                          },
                        ],
                      },
                    },
                  };
                }
                return part;
              }),
            };
            return completedToolInvocation;
          }
          return message;
        });
      });

      await renderZslToCanvas();
      setIsComplete(true);
    },
    onError(error: string) {
      setError(error);
    },
  });

  const onPromptSubmit = useCallback(
    async (localPrompt: string) => {
      setIsComplete(false);
      setStringOutline(""); // When user modify the prompt

      setPrompt(localPrompt);
      setTuningPrompts([]); // Reset tuning prompts for each new AI suggestion
      await handleSubmit(localPrompt);
    },
    [
      handleSubmit,
      setIsComplete,
      setPrompt,
      setStringOutline,
      setTuningPrompts,
    ],
  );

  return {
    onPromptSubmit,
  };
}
